import { useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { useAuthContext } from '../context/AuthContext'
import { useDataOwnerId } from './useDataOwnerId'
import { useToast } from '../components/ui/useToast'
import { playTeamChatSound, isTeamChatMuted } from './teamChatSound'
import { isTeamChatOpen } from './teamChatUiState'

// ─── useTeamChatNotifications ────────────────────────────────────────────────
//
// App-wide listener for the studio Team Chat (team_messages). Subscribes to
// realtime INSERTs scoped to the current studio (owner_id) and pops a toast
// for any message NOT sent by the current user — so a planner gets a heads-up
// even when the floating chat popup is closed.
//
// Mounted once near the root (App.jsx → <TeamChatNotifier />). Realtime
// respects RLS, so the broadcast layer already drops rows from other studios;
// the owner_id filter is a cheap extra narrowing on top.
//
// Defensive by design: no-ops when user or ownerId aren't resolved yet, and
// every showToast call is guarded so a missing ToastProvider (or any realtime
// hiccup) can never throw out of an always-mounted widget.

export function useTeamChatNotifications() {
  const { user, isStudio } = useAuthContext()
  const ownerId = useDataOwnerId()
  const { showToast } = useToast()

  // Keep the latest showToast + user id in refs so the effect doesn't
  // re-subscribe on every render just because these are new references.
  const showToastRef = useRef(showToast)
  useEffect(() => { showToastRef.current = showToast }, [showToast])

  const userIdRef = useRef(user?.id)
  useEffect(() => { userIdRef.current = user?.id }, [user?.id])

  useEffect(() => {
    // Wait until both the auth user and the studio anchor resolve.
    if (!user?.id || !ownerId) return undefined
    // Team Chat is a Studio-only feature — don't subscribe or toast for
    // solo/starter accounts (no team to hear from).
    if (!isStudio) return undefined

    const channel = supabase
      .channel(`team-messages-notify-${ownerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_messages',
          filter: `owner_id=eq.${ownerId}`,
        },
        (payload) => {
          try {
            const row = payload?.new
            if (!row) return
            // Don't toast my own messages.
            if (row.sender_id === userIdRef.current) return
            const name = row.sender_name || 'Teammate'
            const body = (row.body || '').slice(0, 80)
            // A private DM addressed to me (vs a studio-wide broadcast).
            const isDM = !!row.recipient_id && row.recipient_id === userIdRef.current

            // Is the planner already watching the chat? The chat is now a
            // floating popup that can be open on ANY route, so we ask the
            // shared UI-state flag (set by TeamChatFloater) instead of
            // sniffing the pathname.
            const chatOpen = isTeamChatOpen()
            const activeOnChat = chatOpen && !document.hidden

            // In-app toast. Broadcasts: only when the bubble is closed + the tab
            // is visible (else redundant; a hidden tab gets the desktop notif).
            // A private DM to me ALWAYS toasts when visible — even if the bubble
            // is open on a different thread — so a DM can't be silently buried
            // by the conversation filter.
            const show = showToastRef.current
            const shouldToast = !document.hidden && (isDM || !chatOpen)
            if (shouldToast && typeof show === 'function') {
              show(isDM ? `${name} (private): ${body}` : `${name}: ${body}`)
            }

            // Audible chime — unless muted, or they're actively reading the
            // chat (a ping for a message you're watching arrive is just noise).
            if (!isTeamChatMuted() && !activeOnChat) {
              playTeamChatSound()
            }

            // Desktop notification — only when the tab is backgrounded and
            // the user has already granted permission (we never prompt here;
            // the prompt lives on the chat page behind a user gesture).
            try {
              if (
                document.hidden &&
                'Notification' in window &&
                Notification.permission === 'granted'
              ) {
                new Notification(isDM ? `${name} · Private message` : `${name} · Team Chat`, {
                  body: (row.body || '').slice(0, 120),
                  tag: 'planos-team-chat',
                })
              }
            } catch (notifyErr) {
              console.warn('[useTeamChatNotifications] desktop notification failed:', notifyErr)
            }
          } catch (err) {
            // Never let a malformed payload or a missing provider throw
            // out of an always-mounted widget.
            console.warn('[useTeamChatNotifications] toast failed:', err)
          }
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, ownerId, isStudio])
}

export default useTeamChatNotifications
